/* eslint-disable no-underscore-dangle */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Button, Spinner, Badge, Card,
} from 'react-bootstrap';
import BillingoService from '../../../Services/BillingoService';
import { invoicedSummary, formatAmount } from '../../../Utils/billingoDocuments';
import BillingoDocumentModal from '../../../Components/Billingo/BillingoDocumentModal';
import KeyValueRow from '../../../Components/Billingo/KeyValueRow';

// Shows what has already been invoiced for a resolved booking set (value /
// requested / invoiced / remaining) so the user never re-invoices, and launches
// the document flow seeded with the whole set.
function InvoicedSummaryTable({
  bookings, partner = null, basisLabel = '', onInvoiced = () => {},
}) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const refresh = useCallback(async () => {
    if (!bookings.length) { setDocuments([]); return; }
    setLoading(true);
    try {
      setDocuments(await BillingoService.listDocumentsForBookings(bookings));
    } finally {
      setLoading(false);
    }
  }, [bookings]);

  useEffect(() => { refresh(); }, [refresh]);

  const { rows, totals } = invoicedSummary(bookings, documents);
  const currencies = Object.keys(totals);

  if (!bookings.length) return null;

  return (
    <div className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">
          {basisLabel || 'Selected bookings'}
          {' '}
          <Badge bg="light" text="dark">{bookings.length}</Badge>
        </h6>
        <Button onClick={() => setShowModal(true)}>
          <i className="fa fa-file-invoice me-1" />
          Create document
        </Button>
      </div>

      {loading && (
        <div className="text-muted small">
          <Spinner size="sm" animation="border" className="me-2" />
          Loading invoiced totals…
        </div>
      )}

      <Table responsive size="sm" className="align-middle d-none d-md-table">
        <thead className="table-light">
          <tr>
            <th>Order</th>
            <th className="text-end">Value</th>
            <th className="text-end">Requested</th>
            <th className="text-end">Invoiced</th>
            <th className="text-end">Remaining</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.booking_id}>
              <td>{r.order_reference}</td>
              <td className="text-end">{formatAmount(r.value, r.currency)}</td>
              <td className="text-end text-muted">{formatAmount(r.requested, r.currency)}</td>
              <td className="text-end">{formatAmount(r.invoiced, r.currency)}</td>
              <td className="text-end">
                {r.remaining <= 0.005 ? (
                  <Badge bg="success">Done</Badge>
                ) : (
                  <strong>{formatAmount(r.remaining, r.currency)}</strong>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="table-light">
          {currencies.map((cur) => (
            <tr key={cur}>
              <th>
                Total (
                {cur}
                )
              </th>
              <th className="text-end">{formatAmount(totals[cur].value, cur)}</th>
              <th className="text-end text-muted">{formatAmount(totals[cur].requested, cur)}</th>
              <th className="text-end">{formatAmount(totals[cur].invoiced, cur)}</th>
              <th className="text-end">{formatAmount(totals[cur].remaining, cur)}</th>
            </tr>
          ))}
        </tfoot>
      </Table>

      {/* Mobile cards */}
      <div className="d-md-none">
        {rows.map((r) => (
          <Card key={r.booking_id} className="mb-2">
            <Card.Body className="py-2">
              <KeyValueRow
                emphasis
                label={r.order_reference}
                value={formatAmount(r.value, r.currency)}
              />
              <KeyValueRow label="Invoiced" value={formatAmount(r.invoiced, r.currency)} />
              <KeyValueRow
                label="Remaining"
                value={r.remaining <= 0.005
                  ? <Badge bg="success">Done</Badge>
                  : <strong>{formatAmount(r.remaining, r.currency)}</strong>}
              />
            </Card.Body>
          </Card>
        ))}
        {currencies.map((curr) => (
          <KeyValueRow
            key={curr}
            emphasis
            label={`Total remaining (${curr})`}
            value={formatAmount(totals[curr].remaining, curr)}
          />
        ))}
      </div>

      <BillingoDocumentModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onComplete={() => { refresh(); onInvoiced(); }}
        context={{
          mode: 'order',
          order: bookings[0],
          bookings,
          partner,
          existingDocs: documents,
        }}
      />
    </div>
  );
}

export default InvoicedSummaryTable;
